import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-home-btn">
            <ArrowLeft size={16} className="mr-2" />
            Back to Home
          </Button>
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="The Interview Logo" className="w-8 h-8" />
            <h1 className="text-xl font-heading font-bold">The Interview</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="bg-white p-8 md:p-12 border rounded-lg">
          <h1 className="text-4xl font-heading font-bold mb-4">Terms of Service</h1>
          <p className="text-muted-foreground mb-8">Last Updated: August 7, 2026</p>

          <div className="prose prose-slate max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">1. Agreement to Terms</h2>
              <p>
                These Terms of Service ("Terms") constitute a legally binding agreement between you and Nichols Global 
                Business Services, LLC ("Company," "we," "our," or "us") concerning your access to and use of The Interview 
                platform and related services (collectively, the "Services").
              </p>
              <p className="mt-2">
                By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, 
                you may not access or use the Services.
              </p>
              <p className="mt-2">
                Each time you sign in, you reaffirm your agreement to the current Terms and acknowledge our Privacy Policy and Community Safety practices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">2. Eligibility</h2>
              <p>
                You must be at least 18 years old to use our Services. By using the Services, you represent and warrant that:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You are at least 18 years of age</li>
                <li>You have the legal capacity to enter into these Terms</li>
                <li>You will comply with all applicable laws and regulations</li>
                <li>All information you provide is accurate and truthful</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">3. User Accounts</h2>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">3.1 Account Types</h3>
              <p>We offer two types of accounts:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Candidate Accounts:</strong> For job seekers looking for employment opportunities</li>
                <li><strong>Company Accounts:</strong> For employers posting job listings and conducting interviews</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">3.2 Account Registration</h3>
              <p>When creating an account, you agree to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your password and account</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">3.3 Account Termination</h3>
              <p>
                We may restrict, temporarily hold, suspend, or terminate an account for violations of these Terms, fraud, threats to user safety, or misuse of the Services. Automated tools may apply a short temporary hold, but administrators control final account action and users may request review.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">4. Services Description</h2>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">4.1 For Candidates</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Browse and search job listings</li>
                <li>Book video interview slots directly with companies</li>
                <li>Participate in video interviews with screen sharing</li>
                <li>Access community forums and chat</li>
                <li>Purchase interview preparation tools and services</li>
                <li>Connect with mentors</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">4.2 For Companies</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Post job listings with available interview slots</li>
                <li>Conduct video interviews with candidates</li>
                <li>Manage interview schedules</li>
                <li>Boost job listings for increased visibility (paid feature)</li>
                <li>Access candidate profiles and resumes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">5. User Conduct and Responsibilities</h2>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">5.1 Prohibited Activities</h3>
              <p>You agree NOT to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Post false, inaccurate, misleading, or fraudulent information</li>
                <li>Impersonate any person or entity</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Post discriminatory content based on race, gender, religion, age, or disability</li>
                <li>Upload viruses, malware, or malicious code</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Scrape or collect data from the Services without permission</li>
                <li>Use the Services for any illegal purpose</li>
                <li>Spam users with unsolicited communications</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">5.2 Content Standards</h3>
              <p>All content you post must:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Be accurate and not misleading</li>
                <li>Comply with applicable laws and regulations</li>
                <li>Not violate any third-party rights</li>
                <li>Be professional and appropriate</li>
                <li>Not contain offensive or explicit material</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">5.3 Automated and AI-Assisted Safety Moderation</h3>
              <p>
                We use automated and human review to detect possible bullying, harassment, discrimination, threats, sexual exploitation, spam, and other unsafe conduct in written platform content. Flagged content may be blocked and severe or repeated violations may place an account on a temporary hold for administrator review.
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Automated safety results are not used to make hiring or employment decisions</li>
                <li>User reports enter a confidential review queue and do not automatically penalize another account</li>
                <li>Administrators may restore an account, issue a warning, dismiss a report, or extend a hold</li>
                <li>Video and microphone feeds are not analyzed or recorded for safety moderation unless participants separately receive notice and consent to a transcription or recording feature</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">5.4 Safety Review Requests</h3>
              <p>
                To question a safety action or request review, contact safety@theinterviewpros.com from the email associated with your account. Include only information needed to identify the action; never send your password or payment details.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">6. Intellectual Property Rights</h2>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">6.1 Our Property</h3>
              <p>
                The Services, including all content, features, and functionality, are owned by Nichols Global Business 
                Services, LLC and are protected by copyright, trademark, and other intellectual property laws. The Interview 
                name, logo, and all related marks are trademarks of Nichols Global Business Services, LLC.
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-2">6.2 Your Content</h3>
              <p>
                You retain ownership of content you submit to the Services (resumes, profiles, posts, messages). By 
                submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, 
                modify, and display your content for the purpose of providing the Services.
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-2">6.3 Feedback</h3>
              <p>
                Any feedback, suggestions, or ideas you provide about the Services become our property, and we may use 
                them without obligation to you.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">7. Payment Terms</h2>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">7.1 Pricing</h3>
              <p>Certain features of our Services require payment:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Job Boost:</strong> $5.00 per day for promoted job listings</li>
                <li><strong>Marketplace Products:</strong> Prices vary by product ($29-$79)</li>
                <li><strong>Mentorship Sessions:</strong> Prices set by individual mentors</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">7.2 Payment Processing</h3>
              <p>
                Payments are processed securely through Stripe. You agree to pay all fees and charges incurred through 
                your account. All prices are in USD unless otherwise stated.
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-2">7.3 Refund Policy</h3>
              <p>
                Refunds are provided at our discretion. To request a refund, contact support@theinterviewpros.com within
                7 days of purchase. Job boost fees are non-refundable once the boost period has started.
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-2">7.4 Subscription Services</h3>
              <p>
                If you subscribe to recurring services, you will be billed automatically until you cancel. You may cancel 
                at any time through your account settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">8. Third-Party Services</h2>
              <p>
                Our Services integrate with third-party services including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Stripe for payment processing</li>
                <li>Google Calendar for scheduling</li>
                <li>Video conferencing platforms for interviews</li>
                <li>Email service providers</li>
              </ul>
              <p className="mt-2">
                Your use of these third-party services is subject to their respective terms and privacy policies. We are 
                not responsible for the practices of third-party service providers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">9. Disclaimers and Limitations of Liability</h2>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">9.1 Service "As Is"</h3>
              <p>
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR 
                IMPLIED. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-2">9.2 No Employment Guarantee</h3>
              <p>
                We do not guarantee that candidates will find employment or that companies will find suitable candidates. 
                We are a platform connecting job seekers and employers; hiring decisions are made solely by employers.
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-2">9.3 Limitation of Liability</h3>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, NICHOLS GLOBAL BUSINESS SERVICES, LLC SHALL NOT BE LIABLE FOR ANY 
                INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, 
                WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
              </p>
              <p className="mt-2">
                OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE 12 MONTHS PRECEDING THE CLAIM, OR 
                $100, WHICHEVER IS GREATER.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">10. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Nichols Global Business Services, LLC, its officers, 
                directors, employees, and agents from any claims, liabilities, damages, losses, and expenses, including 
                reasonable attorneys' fees, arising out of or related to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your use of the Services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of another party</li>
                <li>Content you submit to the Services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">11. Dispute Resolution</h2>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">11.1 Governing Law</h3>
              <p>
                These Terms are governed by the laws of the State of [Your State], without regard to conflict of law 
                principles.
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-2">11.2 Arbitration</h3>
              <p>
                Any dispute arising from these Terms or the Services shall be resolved through binding arbitration in 
                accordance with the rules of the American Arbitration Association. The arbitration shall take place in 
                [Your State], and the arbitrator's decision shall be final and binding.
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-2">11.3 Class Action Waiver</h3>
              <p>
                You agree to resolve disputes with us on an individual basis and waive the right to participate in any 
                class action or representative proceeding.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">12. Modifications to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will notify you of material changes by posting 
                the updated Terms on this page and updating the "Last Updated" date. Your continued use of the Services 
                after changes become effective constitutes your acceptance of the revised Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">13. Termination</h2>
              <p>
                We may suspend or terminate your access to the Services at any time, with or without cause or notice. 
                Upon termination, your right to use the Services immediately ceases. Sections that by their nature should 
                survive termination shall survive, including payment obligations, warranty disclaimers, and limitations 
                of liability.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">14. General Provisions</h2>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">14.1 Entire Agreement</h3>
              <p>
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and us regarding 
                the Services.
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-2">14.2 Severability</h3>
              <p>
                If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in 
                full force and effect.
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-2">14.3 Waiver</h3>
              <p>
                Our failure to enforce any provision of these Terms shall not constitute a waiver of that provision or 
                any other provision.
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-2">14.4 Assignment</h3>
              <p>
                You may not assign or transfer these Terms without our prior written consent. We may assign these Terms 
                without restriction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">15. Contact Information</h2>
              <p>
                For questions about these Terms or the Services, contact us:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded border">
                <p><strong>Nichols Global Business Services, LLC</strong></p>
                <p>The Interview Platform</p>
                <p className="mt-2">Email: legal@theinterviewpros.com</p>
                <p>Email: support@theinterviewpros.com</p>
                <p>Website: www.theinterviewpros.com</p>
              </div>
            </section>

            <div className="mt-12 pt-8 border-t">
              <p className="text-sm text-muted-foreground">
                © 2025 Nichols Global Business Services, LLC. All rights reserved. The Interview is a trademark of 
                Nichols Global Business Services, LLC.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
